// MOZAIC Platform — Public marketing homepage (mozaicplatform.com /)
import { Link } from 'react-router-dom';

export default function HomePage() {
  return (
    <div className="mz-hp">
      <style>{HOMEPAGE_CSS}</style>

      <nav className="mz-hp-nav">
        <div className="mz-hp-nav-logo">
          <img
            src="/mozaic-logo.png"
            alt="MOZAIC"
            className="mz-hp-logo-img"
          />
          <span className="mz-hp-logo-text">
            MOZAIC<span>.</span>
          </span>
        </div>
        <div className="mz-hp-nav-links">
          <a href="#platform">Platform</a>
          <a href="#edsr">eDSR</a>
          <a href="#integrations">Integrations</a>
          <a href="#security">Security</a>
          <a href="#pricing">Pricing</a>
        </div>
        <div className="mz-hp-nav-right">
          <Link to="/login" className="mz-hp-btn-ghost">
            Sign in
          </Link>
          <a
            href="mailto:hello@mozaicplatform.com?subject=MOZAIC%20Early%20Adopter%20Request"
            className="mz-hp-btn-primary"
          >
            Request access
          </a>
        </div>
      </nav>

      <section className="mz-hp-hero" id="platform">
        <div className="mz-hp-hero-badge">
          <div className="mz-hp-badge-dot" />
          Now Accepting Early Adopters
        </div>
        <h1>
          HSE intelligence that <span className="mz-hp-accent">learns from every shift</span>
        </h1>
        <p>
          MOZAIC is the field safety platform built for oil &amp; gas. Collect, analyze, and act on
          operational data — with AI that gets smarter every time a report is filed.
        </p>
        <div className="mz-hp-hero-actions">
          <a
            href="mailto:hello@mozaicplatform.com?subject=MOZAIC%20Early%20Adopter%20Request"
            className="mz-hp-btn-lg mz-hp-primary"
          >
            Request early access
          </a>
          <a href="#modules" className="mz-hp-btn-lg mz-hp-outline">
            See the platform ↓
          </a>
        </div>
      </section>

      <div className="mz-hp-section" id="modules">
        <div className="mz-hp-section-header">
          <div className="mz-hp-section-label">Platform modules</div>
          <div className="mz-hp-section-title">
            Every tool your operation needs,
            <br />
            turned on when you're ready
          </div>
          <div className="mz-hp-section-sub">
            Activate modules as your team scales. Each one connects to the shared intelligence layer
            — so insights compound across your entire operation.
          </div>
        </div>

        <div className="mz-hp-modules-grid">
          {MODULES.map((m) => (
            <div
              key={m.title}
              className={`mz-hp-module-card ${m.active ? 'mz-hp-module-active' : ''} ${
                m.dim ? 'mz-hp-module-dim' : ''
              }`}
              style={{ ['--card-accent' as string]: m.accent }}
            >
              <div className={`mz-hp-module-tag ${m.tagClass}`}>
                {m.tagDot && (
                  <span
                    className="mz-hp-module-tag-dot"
                    style={{ background: m.tagDot }}
                  />
                )}
                {m.tag}
              </div>
              <div className="mz-hp-module-icon" style={{ background: m.iconBg }}>
                {m.icon}
              </div>
              <div className="mz-hp-module-title">{m.title}</div>
              <div className="mz-hp-module-desc">{m.desc}</div>
              <div className="mz-hp-module-features">
                {m.features.map((f) => (
                  <div key={f} className="mz-hp-module-feat">
                    {f}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mz-hp-ai-strip">
        <div className="mz-hp-ai-inner">
          <div className="mz-hp-ai-content">
            <div className="mz-hp-section-label">AI intelligence layer</div>
            <div className="mz-hp-section-title mz-hp-ai-title">
              The model gets smarter with every report filed
            </div>
            <p>
              Every eDSR submitted to MOZAIC is a training signal. Your data stays isolated and
              sovereign in your tenant — but collective patterns across all operators make the
              model's predictions sharper for everyone.
            </p>
            <div className="mz-hp-ai-chips">
              {AI_CHIPS.map((c) => (
                <span key={c} className="mz-hp-ai-chip">
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="mz-hp-ai-viz">
              <div className="mz-hp-ai-viz-label">Live risk assessment — Demo data</div>
              <div className="mz-hp-risk-bars">
                {RISK_ROWS.map((r) => (
                  <div key={r.name} className="mz-hp-risk-row">
                    <div className="mz-hp-risk-name">{r.name}</div>
                    <div className="mz-hp-risk-track">
                      <div
                        className={`mz-hp-risk-fill mz-hp-risk-${r.level}`}
                        style={{ width: `${r.value}%` }}
                      />
                    </div>
                    <div className="mz-hp-risk-val" style={{ color: r.color }}>
                      {r.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mz-hp-ai-insight">
                <div className="mz-hp-ai-insight-label">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.5" stroke="#00C896" />
                    <path d="M6 3v3l2 1.5" stroke="#00C896" strokeLinecap="round" />
                  </svg>
                  AI insight — 11:42 AM
                </div>
                <p>
                  Well C-07 SSE% is 42% — above threshold. SimOps risk elevated. Three JSAs pending
                  review. Recommend supervisor check-in before afternoon shift.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mz-hp-integrations" id="integrations">
        <div className="mz-hp-section-header">
          <div className="mz-hp-section-label">Integrations</div>
          <div className="mz-hp-section-title">Connects to what you already run</div>
          <div className="mz-hp-section-sub">
            Already have a POB system, ERP, or compliance tool? MOZAIC adds AI intelligence without
            ripping out existing infrastructure.
          </div>
        </div>
        <div className="mz-hp-int-grid">
          {INTEGRATIONS.map((i) => (
            <div
              key={i.name}
              className={`mz-hp-int-card ${i.dashed ? 'mz-hp-int-dashed' : ''}`}
            >
              <div className="mz-hp-int-icon" style={{ background: i.iconBg }}>
                {i.icon}
              </div>
              <div className="mz-hp-int-name" style={i.dashed ? { color: 'var(--mz-muted)' } : undefined}>
                {i.name}
              </div>
              <div className="mz-hp-int-type">{i.type}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mz-hp-partner-strip">
        <div className="mz-hp-partner-inner">
          <div className="mz-hp-partner-heading">
            Built on enterprise-grade infrastructure
          </div>
          <div className="mz-hp-trust-pills">
            <span className="mz-hp-trust-pill">
              <span className="mz-hp-trust-icon">🔒</span>
              SOC 2 Type II Infrastructure
            </span>
            <span className="mz-hp-trust-pill">
              <span className="mz-hp-trust-icon">🔐</span>
              End-to-End Encrypted
            </span>
            <span className="mz-hp-trust-pill">
              <span className="mz-hp-trust-icon">🏢</span>
              Multi-Tenant Isolated
            </span>
            <span className="mz-hp-trust-pill">
              <span className="mz-hp-trust-icon">📋</span>
              Audit Logging Built-In
            </span>
          </div>
        </div>
      </div>

      <div className="mz-hp-security" id="security">
        <div className="mz-hp-security-inner">
          <div className="mz-hp-security-badge">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#00C896"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            SOC 2 Type II
          </div>
          <h3 className="mz-hp-security-title">
            SOC 2 Type II Compliant Infrastructure
          </h3>
          <p className="mz-hp-security-body">
            MOZAIC operates on SOC 2 Type II certified infrastructure. Your operational data is
            encrypted in transit and at rest, isolated per tenant at the database layer, and
            protected by role-based access controls. We never share your data across operators.
          </p>
        </div>
      </div>

      <div className="mz-hp-pricing" id="pricing">
        <div className="mz-hp-section-header mz-hp-pricing-header">
          <div className="mz-hp-section-label">Pricing</div>
          <div className="mz-hp-section-title">Simple, Transparent Pricing</div>
        </div>
        <div className="mz-hp-pricing-contact">
          <div className="mz-hp-contact-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#00C896"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 6l-10 7L2 6" />
            </svg>
          </div>
          <div className="mz-hp-contact-heading">Enterprise Pricing</div>
          <p className="mz-hp-contact-body">
            MOZAIC is priced per operator based on project count and user volume. Contact us for a
            proposal tailored to your organization.
          </p>
          <a
            href="mailto:hello@mozaicplatform.com"
            className="mz-hp-btn-lg mz-hp-primary mz-hp-contact-btn"
          >
            Request Pricing →
          </a>
        </div>
      </div>

      <div className="mz-hp-cta" id="cta">
        <h2>Ready to build the safest field operation across your assets?</h2>
        <p>
          MOZAIC is currently accepting early-adopter operators and contractors. Spots are limited.
        </p>
        <div className="mz-hp-hero-actions">
          <a
            href="mailto:hello@mozaicplatform.com?subject=MOZAIC%20Early%20Adopter%20Request"
            className="mz-hp-btn-lg mz-hp-primary"
          >
            Request access
          </a>
          <a
            href="mailto:hello@mozaicplatform.com?subject=MOZAIC%20Demo%20Request"
            className="mz-hp-btn-lg mz-hp-outline"
          >
            Schedule a demo
          </a>
        </div>
      </div>

      <footer className="mz-hp-footer">
        <div className="mz-hp-footer-inner">
          <div className="mz-hp-footer-brand">
            <img
              src="/mozaic-logo.png"
              alt="MOZAIC"
              className="mz-hp-footer-logo"
            />
            <div className="mz-hp-footer-brand-text">
              <div className="mz-hp-footer-copy">
                © 2026 Datisol, LLC. All rights reserved.
              </div>
              <div className="mz-hp-footer-attribution">
                MOZAIC is a product of Datisol, LLC
              </div>
            </div>
          </div>

          <div className="mz-hp-footer-links">
            <a href="#privacy">Privacy</a>
            <a href="#security">Security</a>
            <a href="#data">Data handling</a>
            <a href="mailto:hello@mozaicplatform.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

type ModuleCard = {
  title: string;
  desc: string;
  features: string[];
  tag: string;
  tagClass: string;
  tagDot?: string;
  icon: string;
  iconBg: string;
  accent: string;
  active?: boolean;
  dim?: boolean;
};

const MODULES: ModuleCard[] = [
  {
    title: 'eDSR — Daily Safety Report',
    desc: 'Digital field safety reports with AI-assisted narrative validation, risk scoring, and offline-first mobile capture.',
    features: [
      'AI narrative analysis',
      'Offline-first mobile',
      'Auto risk scoring',
      'Stop Work Authority',
      'JSA integration',
    ],
    tag: 'Live',
    tagClass: 'mz-hp-tag-live',
    tagDot: '#00C896',
    icon: '📋',
    iconBg: 'rgba(0,200,150,0.12)',
    accent: '#00C896',
    active: true,
  },
  {
    title: 'PROJO — Project Operations',
    desc: 'Project lifecycle management for field operations. Planning, permitting, workforce coordination, and daily operations — integrated.',
    features: [
      'Project & permit tracking',
      'Crew scheduling',
      'Activity forecasting',
      'eDSR integration',
    ],
    tag: 'Coming soon',
    tagClass: 'mz-hp-tag-soon',
    icon: '🏗️',
    iconBg: 'rgba(26,111,232,0.12)',
    accent: '#6BA8FF',
  },
  {
    title: 'POB — Personnel on Board',
    desc: 'Real-time headcount, muster tracking, and crew manifest management for offshore and remote operations.',
    features: [
      'Live muster board',
      'Emergency roll call',
      'Certifications & quals',
      'SSE% monitoring',
      'API sync to existing systems',
    ],
    tag: 'Coming soon',
    tagClass: 'mz-hp-tag-soon',
    icon: '👥',
    iconBg: 'rgba(245,158,11,0.12)',
    accent: '#F59E0B',
  },
  {
    title: 'Predictive Analytics',
    desc: 'Pattern-based incident probability scoring, powered by the MOZAIC proprietary model trained on cross-operator safety data.',
    features: [
      'Incident probability scores',
      'Crew risk matching',
      'SimOps alerts',
      'Trend dashboards',
    ],
    tag: 'Coming soon',
    tagClass: 'mz-hp-tag-soon',
    icon: '📊',
    iconBg: 'rgba(192,132,252,0.12)',
    accent: '#C084FC',
  },
  {
    title: 'Human Factors Intelligence',
    desc: 'Identify fatigue, crew composition risk, and behavioral patterns before they become incidents. Built on validated HFI frameworks.',
    features: [
      'Fatigue risk modeling',
      'Crew behavioral analytics',
      'Leading indicator tracking',
    ],
    tag: 'Coming soon',
    tagClass: 'mz-hp-tag-soon',
    icon: '🧠',
    iconBg: 'rgba(245,158,11,0.1)',
    accent: '#F59E0B',
  },
  {
    title: 'Safety Knowledge System',
    desc: 'AI-searchable repository of procedures, lessons learned, and regulatory guidance. Context-aware answers at the point of work.',
    features: ['Procedure Q&A', 'Incident library', 'Reg guidance engine'],
    tag: 'Planned',
    tagClass: 'mz-hp-tag-planned',
    icon: '📚',
    iconBg: 'rgba(255,255,255,0.06)',
    accent: '#94A3B8',
  },
  {
    title: 'Third-Party Integrations',
    desc: 'Already have a POB, ERP, or compliance system? Connect it to MOZAIC via API to add AI insights without replacing what works.',
    features: [
      'REST & webhook APIs',
      'Bring your own POB system',
      'ERP data sync',
      'Custom connectors',
    ],
    tag: 'API Connect',
    tagClass: 'mz-hp-tag-api',
    icon: '🔌',
    iconBg: 'rgba(26,111,232,0.1)',
    accent: '#6BA8FF',
  },
  {
    title: 'Executive Intelligence',
    desc: 'Tenant-wide safety performance at a glance. Cross-site benchmarking, AI-generated narratives, and board-ready reporting.',
    features: [
      'Enterprise dashboard',
      'Cross-operator benchmarks',
      'AI narrative reports',
    ],
    tag: 'Planned',
    tagClass: 'mz-hp-tag-planned',
    icon: '🌐',
    iconBg: 'rgba(255,255,255,0.05)',
    accent: '#94A3B8',
    dim: true,
  },
];

const AI_CHIPS = [
  'Narrative validation',
  'Risk tag suggestions',
  'Daily summaries',
  'Pattern detection',
  'Incident probability',
  'Crew-risk matching',
];

const RISK_ROWS = [
  { name: 'Well A-14', value: 28, level: 'low', color: '#00C896' },
  { name: 'Platform B-3', value: 61, level: 'mid', color: '#F59E0B' },
  { name: 'Well C-07', value: 84, level: 'high', color: '#EF4444' },
  { name: 'Pipeline D', value: 19, level: 'low', color: '#00C896' },
  { name: 'Compressor E', value: 47, level: 'mid', color: '#F59E0B' },
] as const;

const INTEGRATIONS = [
  { name: 'SAP', type: 'ERP / Workforce', icon: '📦', iconBg: 'rgba(255,153,0,0.1)' },
  { name: 'Maximo', type: 'Asset Management', icon: '📊', iconBg: 'rgba(26,111,232,0.1)' },
  { name: 'Enablon', type: 'EHS Platform', icon: '🛡️', iconBg: 'rgba(0,200,150,0.1)' },
  { name: 'Intelex', type: 'Quality & Safety', icon: '📋', iconBg: 'rgba(192,132,252,0.1)' },
  { name: 'Azure AD', type: 'Identity / SSO', icon: '🔑', iconBg: 'rgba(245,158,11,0.1)' },
  { name: 'Custom POB', type: 'Muster / Headcount', icon: '📡', iconBg: 'rgba(255,255,255,0.06)' },
  { name: 'Power BI', type: 'Reporting / BI', icon: '📈', iconBg: 'rgba(26,111,232,0.08)' },
  {
    name: 'Your system',
    type: 'Custom API connector',
    icon: '+',
    iconBg: 'rgba(255,255,255,0.04)',
    dashed: true,
  },
];

const HOMEPAGE_CSS = `
.mz-hp {
  --mz-navy: #0A1628;
  --mz-navy-2: #0F1E38;
  --mz-navy-3: #152342;
  --mz-teal: #00C896;
  --mz-teal-dim: #00A07A;
  --mz-blue: #1A6FE8;
  --mz-amber: #F59E0B;
  --mz-red: #EF4444;
  --mz-text: #E8EFF8;
  --mz-muted: #8DA0B8;
  --mz-border: rgba(255,255,255,0.08);
  --mz-card: rgba(255,255,255,0.04);
  --mz-card-hover: rgba(255,255,255,0.07);
  background: var(--mz-navy);
  color: var(--mz-text);
  font-family: 'Geist Variable', ui-sans-serif, system-ui, sans-serif;
  min-height: 100vh;
  min-width: 100vw;
  position: absolute;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.mz-hp * { box-sizing: border-box; }
.mz-hp a { color: inherit; text-decoration: none; }

.mz-hp-nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 2rem; height: 60px;
  border-bottom: 1px solid var(--mz-border);
  position: sticky; top: 0; z-index: 100;
  background: rgba(10,22,40,0.95);
  backdrop-filter: blur(8px);
}
.mz-hp-nav-logo { display: flex; align-items: center; gap: 10px; }
.mz-hp-logo-img {
  height: 32px; width: auto; display: block;
}
.mz-hp-logo-text { font-size: 18px; font-weight: 500; letter-spacing: -0.3px; color: var(--mz-text); }
.mz-hp-logo-text span { color: var(--mz-teal); }
.mz-hp-nav-links { display: flex; align-items: center; gap: 28px; }
.mz-hp-nav-links a { font-size: 14px; color: var(--mz-muted); transition: color 0.2s; cursor: pointer; }
.mz-hp-nav-links a:hover { color: var(--mz-text); }
.mz-hp-nav-right { display: flex; align-items: center; gap: 12px; }
.mz-hp-btn-ghost {
  font-size: 13px; color: var(--mz-muted); background: none;
  border: 1px solid var(--mz-border); padding: 7px 16px; border-radius: 6px;
  cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center;
}
.mz-hp-btn-ghost:hover { color: var(--mz-text); border-color: rgba(255,255,255,0.2); }
.mz-hp-btn-primary {
  font-size: 13px; color: var(--mz-navy); background: var(--mz-teal);
  border: none; padding: 7px 18px; border-radius: 6px; cursor: pointer;
  font-weight: 500; transition: all 0.2s; display: inline-flex; align-items: center;
}
.mz-hp-btn-primary:hover { background: #00DFA8; }

.mz-hp-hero {
  padding: 80px 2rem 64px; text-align: center;
  max-width: 1100px; margin: 0 auto;
}
.mz-hp-hero-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(0,200,150,0.1); border: 1px solid rgba(0,200,150,0.25);
  border-radius: 100px; padding: 5px 14px; font-size: 12px;
  color: var(--mz-teal); margin-bottom: 28px;
}
.mz-hp-badge-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--mz-teal); animation: mz-hp-pulse 2s infinite;
}
@keyframes mz-hp-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
.mz-hp-hero h1 {
  font-size: 48px; font-weight: 500; line-height: 1.15;
  letter-spacing: -0.8px; margin: 0 0 20px;
}
.mz-hp-hero h1 .mz-hp-accent { color: var(--mz-teal); }
.mz-hp-hero p {
  font-size: 18px; color: var(--mz-muted); line-height: 1.7;
  max-width: 620px; margin: 0 auto 36px;
}
.mz-hp-hero-actions {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; flex-wrap: wrap;
}
.mz-hp-btn-lg {
  font-size: 15px; padding: 12px 28px; border-radius: 8px;
  cursor: pointer; font-weight: 500; transition: all 0.2s; border: none;
  display: inline-flex; align-items: center; justify-content: center;
}
.mz-hp-btn-lg.mz-hp-primary { background: var(--mz-teal); color: var(--mz-navy); }
.mz-hp-btn-lg.mz-hp-primary:hover { background: #00DFA8; transform: translateY(-1px); }
.mz-hp-btn-lg.mz-hp-outline {
  background: none; color: var(--mz-text);
  border: 1px solid rgba(255,255,255,0.18);
}
.mz-hp-btn-lg.mz-hp-outline:hover {
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.04);
}

.mz-hp-stats-bar {
  border-top: 1px solid var(--mz-border);
  border-bottom: 1px solid var(--mz-border);
  background: var(--mz-navy-2); padding: 0 2rem;
}
.mz-hp-stats-inner {
  max-width: 1100px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(4, 1fr);
}
.mz-hp-stat-item {
  padding: 24px 20px; text-align: center;
  border-right: 1px solid var(--mz-border);
}
.mz-hp-stat-item:last-child { border-right: none; }
.mz-hp-stat-num { font-size: 28px; font-weight: 500; color: var(--mz-teal); margin-bottom: 4px; }
.mz-hp-stat-label {
  font-size: 12px; color: var(--mz-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
}

.mz-hp-section { padding: 72px 2rem; max-width: 1100px; margin: 0 auto; }
.mz-hp-section-header { margin-bottom: 48px; }
.mz-hp-section-label {
  font-size: 12px; color: var(--mz-teal);
  text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px;
}
.mz-hp-section-title {
  font-size: 32px; font-weight: 500; letter-spacing: -0.4px; margin-bottom: 12px;
}
.mz-hp-section-sub {
  font-size: 16px; color: var(--mz-muted); line-height: 1.7; max-width: 540px;
}

.mz-hp-modules-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
}
.mz-hp-module-card {
  background: var(--mz-card); border: 1px solid var(--mz-border);
  border-radius: 12px; padding: 24px; cursor: pointer;
  transition: all 0.25s; position: relative; overflow: hidden;
}
.mz-hp-module-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: var(--card-accent, var(--mz-teal));
  opacity: 0; transition: opacity 0.25s;
}
.mz-hp-module-card:hover {
  background: var(--mz-card-hover);
  border-color: var(--card-accent, var(--mz-teal));
  transform: translateY(-2px);
}
.mz-hp-module-card:hover::before { opacity: 1; }
.mz-hp-module-card.mz-hp-module-active { border-color: rgba(0,200,150,0.3); }
.mz-hp-module-card.mz-hp-module-active::before { opacity: 1; }
.mz-hp-module-card.mz-hp-module-dim { opacity: 0.6; }

.mz-hp-module-tag {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; padding: 3px 10px; border-radius: 100px; margin-bottom: 14px;
}
.mz-hp-module-tag-dot { width: 5px; height: 5px; border-radius: 50%; }
.mz-hp-tag-live { background: rgba(0,200,150,0.12); color: var(--mz-teal); border: 1px solid rgba(0,200,150,0.25); }
.mz-hp-tag-soon { background: rgba(245,158,11,0.1); color: var(--mz-amber); border: 1px solid rgba(245,158,11,0.25); }
.mz-hp-tag-api { background: rgba(26,111,232,0.1); color: #6BA8FF; border: 1px solid rgba(26,111,232,0.25); }
.mz-hp-tag-planned { background: rgba(255,255,255,0.05); color: var(--mz-muted); border: 1px solid var(--mz-border); }
.mz-hp-tag-discussion { background: rgba(34,197,94,0.08); color: #4ADE80; border: 1px solid rgba(74,222,128,0.2); }

.mz-hp-module-icon {
  width: 42px; height: 42px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 14px; font-size: 20px;
}
.mz-hp-module-title { font-size: 16px; font-weight: 500; margin-bottom: 8px; }
.mz-hp-module-desc {
  font-size: 13px; color: var(--mz-muted);
  line-height: 1.6; margin-bottom: 16px;
}
.mz-hp-module-features { display: flex; flex-direction: column; gap: 5px; }
.mz-hp-module-feat {
  font-size: 12px; color: var(--mz-muted);
  display: flex; align-items: center; gap: 7px;
}
.mz-hp-module-feat::before {
  content: ''; width: 4px; height: 4px; border-radius: 50%;
  background: var(--card-accent, var(--mz-teal)); flex-shrink: 0;
}

.mz-hp-ai-strip {
  background: var(--mz-navy-2);
  border-top: 1px solid var(--mz-border);
  border-bottom: 1px solid var(--mz-border);
  padding: 72px 2rem;
}
.mz-hp-ai-inner {
  max-width: 1100px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center;
}
.mz-hp-ai-title { font-size: 28px; margin-bottom: 16px; }
.mz-hp-ai-content p {
  font-size: 15px; color: var(--mz-muted);
  line-height: 1.7; margin-bottom: 24px;
}
.mz-hp-ai-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.mz-hp-ai-chip {
  font-size: 12px; background: rgba(0,200,150,0.08);
  border: 1px solid rgba(0,200,150,0.2); color: var(--mz-teal);
  padding: 5px 12px; border-radius: 100px;
}
.mz-hp-ai-viz {
  background: rgba(255,255,255,0.03);
  border: 1px solid var(--mz-border);
  border-radius: 12px; padding: 20px;
}
.mz-hp-ai-viz-label {
  font-size: 11px; color: var(--mz-muted);
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 16px;
}
.mz-hp-risk-bars { display: flex; flex-direction: column; gap: 12px; }
.mz-hp-risk-row { display: flex; align-items: center; gap: 12px; }
.mz-hp-risk-name { font-size: 12px; color: var(--mz-muted); width: 90px; flex-shrink: 0; }
.mz-hp-risk-track {
  flex: 1; height: 6px; background: rgba(255,255,255,0.07);
  border-radius: 3px; overflow: hidden;
}
.mz-hp-risk-fill { height: 100%; border-radius: 3px; }
.mz-hp-risk-low { background: var(--mz-teal); }
.mz-hp-risk-mid { background: var(--mz-amber); }
.mz-hp-risk-high { background: var(--mz-red); }
.mz-hp-risk-val { font-size: 12px; font-weight: 500; width: 32px; text-align: right; flex-shrink: 0; }
.mz-hp-ai-insight {
  margin-top: 16px; padding: 12px 14px;
  background: rgba(0,200,150,0.06);
  border: 1px solid rgba(0,200,150,0.15);
  border-radius: 8px;
}
.mz-hp-ai-insight-label {
  font-size: 11px; color: var(--mz-teal); margin-bottom: 5px;
  display: flex; align-items: center; gap: 6px;
}
.mz-hp-ai-insight p { font-size: 13px; color: var(--mz-muted); line-height: 1.5; margin: 0; }

.mz-hp-integrations { padding: 72px 2rem; max-width: 1100px; margin: 0 auto; }
.mz-hp-int-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 16px; margin-top: 40px;
}
.mz-hp-int-card {
  background: var(--mz-card); border: 1px solid var(--mz-border);
  border-radius: 10px; padding: 20px; text-align: center;
  transition: all 0.2s;
}
.mz-hp-int-card:hover {
  background: var(--mz-card-hover);
  border-color: rgba(255,255,255,0.15);
}
.mz-hp-int-dashed { border-style: dashed; }
.mz-hp-int-icon {
  width: 44px; height: 44px; border-radius: 10px;
  margin: 0 auto 12px; display: flex; align-items: center;
  justify-content: center; font-size: 22px;
}
.mz-hp-int-name { font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.mz-hp-int-type { font-size: 12px; color: var(--mz-muted); }

.mz-hp-partner-strip {
  background: var(--mz-navy-2);
  border-top: 1px solid var(--mz-border);
  padding: 48px 2rem;
}
.mz-hp-partner-inner { max-width: 1100px; margin: 0 auto; }
.mz-hp-partner-heading {
  font-size: 13px; color: var(--mz-muted); text-align: center;
  margin-bottom: 28px; text-transform: uppercase; letter-spacing: 0.5px;
}
.mz-hp-partner-logos {
  display: flex; align-items: center; justify-content: center;
  gap: 48px; flex-wrap: wrap;
}
.mz-hp-partner-logo {
  font-size: 14px; font-weight: 500;
  color: rgba(255,255,255,0.25);
  letter-spacing: 1px; text-transform: uppercase;
  transition: color 0.2s;
}
.mz-hp-partner-logo:hover { color: rgba(255,255,255,0.5); }

.mz-hp-trust-pills {
  display: flex; align-items: center; justify-content: center;
  gap: 14px; flex-wrap: wrap;
}
.mz-hp-trust-pill {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 18px; border-radius: 100px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(0,200,150,0.25);
  font-size: 13px; color: var(--mz-text);
  letter-spacing: 0.2px;
}
.mz-hp-trust-icon { font-size: 14px; line-height: 1; }

.mz-hp-footer-brand-text { display: flex; flex-direction: column; gap: 4px; }
.mz-hp-footer-attribution {
  font-size: 12px; color: var(--mz-muted); opacity: 0.7;
}
.mz-hp-partner-special { display: flex; align-items: center; gap: 8px; }
.mz-hp-partner-badge {
  font-size: 10px; background: rgba(245,158,11,0.15);
  color: var(--mz-amber); border: 1px solid rgba(245,158,11,0.3);
  padding: 2px 8px; border-radius: 100px;
}

.mz-hp-security {
  padding: 56px 2rem; max-width: 1100px; margin: 0 auto;
  border-top: 1px solid var(--mz-border);
}
.mz-hp-security-inner {
  max-width: 720px; margin: 0 auto; text-align: center;
}
.mz-hp-security-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px; border-radius: 100px;
  background: rgba(0,200,150,0.08);
  border: 1px solid rgba(0,200,150,0.3);
  font-size: 12px; color: var(--mz-teal);
  letter-spacing: 0.4px; margin-bottom: 16px;
}
.mz-hp-security-title {
  font-size: 28px; font-weight: 500; letter-spacing: -0.5px;
  margin: 0 0 14px; color: var(--mz-text);
}
.mz-hp-security-body {
  font-size: 15px; line-height: 1.6; color: var(--mz-muted); margin: 0;
}

.mz-hp-pricing-contact {
  background: var(--mz-card); border: 1px solid var(--mz-border);
  border-radius: 14px; padding: 36px 32px;
  max-width: 480px; margin: 0 auto; text-align: center;
}
.mz-hp-contact-icon {
  width: 52px; height: 52px; border-radius: 12px;
  background: rgba(0,200,150,0.1);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 18px;
}
.mz-hp-contact-heading {
  font-size: 22px; font-weight: 500; letter-spacing: -0.3px;
  margin-bottom: 10px; color: var(--mz-text);
}
.mz-hp-contact-body {
  font-size: 14px; line-height: 1.6; color: var(--mz-muted);
  margin: 0 0 22px;
}
.mz-hp-contact-btn { display: inline-flex; }

.mz-hp-footer-brand { display: flex; align-items: center; gap: 14px; }
.mz-hp-footer-logo {
  height: 40px; width: auto; display: block;
}

.mz-hp-pricing { padding: 72px 2rem; max-width: 1100px; margin: 0 auto; }
.mz-hp-pricing-header { text-align: center; max-width: 560px; margin: 0 auto 48px; }
.mz-hp-pricing-header .mz-hp-section-sub { margin: 0 auto; }
.mz-hp-pricing-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 20px; margin-top: 48px;
}
.mz-hp-price-card {
  background: var(--mz-card); border: 1px solid var(--mz-border);
  border-radius: 14px; padding: 28px; transition: all 0.2s;
}
.mz-hp-price-card.mz-hp-price-featured {
  background: rgba(0,200,150,0.06);
  border-color: rgba(0,200,150,0.35);
}
.mz-hp-price-card:hover { transform: translateY(-2px); }
.mz-hp-price-tier {
  font-size: 12px; color: var(--mz-muted);
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;
}
.mz-hp-price-num {
  font-size: 36px; font-weight: 500; letter-spacing: -0.5px; margin-bottom: 4px;
}
.mz-hp-price-num span { font-size: 16px; font-weight: 400; color: var(--mz-muted); }
.mz-hp-price-desc {
  font-size: 13px; color: var(--mz-muted); margin-bottom: 24px;
  padding-bottom: 20px; border-bottom: 1px solid var(--mz-border); line-height: 1.5;
}
.mz-hp-price-features {
  display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px;
}
.mz-hp-price-feat {
  font-size: 13px; display: flex; align-items: center; gap: 9px;
}
.mz-hp-feat-check {
  width: 16px; height: 16px; border-radius: 50%;
  background: rgba(0,200,150,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.mz-hp-price-btn {
  width: 100%; padding: 11px; border-radius: 8px;
  font-size: 14px; font-weight: 500; cursor: pointer;
  transition: all 0.2s; border: none;
  display: inline-flex; align-items: center; justify-content: center;
}
.mz-hp-price-btn.mz-hp-price-outline {
  background: none; color: var(--mz-text);
  border: 1px solid rgba(255,255,255,0.18);
}
.mz-hp-price-btn.mz-hp-price-outline:hover {
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.04);
}
.mz-hp-price-btn.mz-hp-price-teal { background: var(--mz-teal); color: var(--mz-navy); }
.mz-hp-price-btn.mz-hp-price-teal:hover { background: #00DFA8; }

.mz-hp-cta { padding: 80px 2rem; text-align: center; max-width: 700px; margin: 0 auto; }
.mz-hp-cta h2 {
  font-size: 36px; font-weight: 500; letter-spacing: -0.4px; margin: 0 0 16px;
}
.mz-hp-cta p {
  font-size: 17px; color: var(--mz-muted);
  margin: 0 0 36px; line-height: 1.7;
}

.mz-hp-footer { border-top: 1px solid var(--mz-border); padding: 40px 2rem; }
.mz-hp-footer-inner {
  max-width: 1100px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
}
.mz-hp-footer-copy { font-size: 13px; color: var(--mz-muted); }
.mz-hp-footer-links { display: flex; gap: 24px; }
.mz-hp-footer-links a { font-size: 13px; color: var(--mz-muted); }
.mz-hp-footer-links a:hover { color: var(--mz-text); }

@media (max-width: 900px) {
  .mz-hp-nav { padding: 0 1rem; }
  .mz-hp-nav-links { display: none; }
  .mz-hp-hero h1 { font-size: 36px; }
  .mz-hp-stats-inner { grid-template-columns: repeat(2, 1fr); }
  .mz-hp-stat-item:nth-child(2) { border-right: none; }
  .mz-hp-modules-grid { grid-template-columns: repeat(2, 1fr); }
  .mz-hp-ai-inner { grid-template-columns: 1fr; gap: 32px; }
  .mz-hp-int-grid { grid-template-columns: repeat(2, 1fr); }
  .mz-hp-pricing-grid { grid-template-columns: 1fr; }
  .mz-hp-footer-inner { flex-direction: column; gap: 16px; }
}
@media (max-width: 560px) {
  .mz-hp-modules-grid { grid-template-columns: 1fr; }
  .mz-hp-hero h1 { font-size: 30px; }
  .mz-hp-section-title { font-size: 26px; }
}
`;
